import { api } from '../api/client';

import { API_COLLECTIONS_URL } from '../utils';

/**
 * ACCEPT AND REJECT RULES on a public form or an intake link (KPR-482;
 * keepr-api docs/SCHEMA.md § Accept and reject rules). Two calls, the same
 * for both kinds — the source names which:
 *
 *   `{ kind: 'form', id }`  → /api/collections/{c}/public-forms/{id}/review-rules
 *   `{ kind: 'link', id }`  → /api/collections/{c}/share-links/{id}/review-rules
 *
 * Both are a manager's acts in a SESSION (the API refuses a key), and both are
 * refused 409 `public_forms_off` while the server's public forms switch is off:
 * the editor is offered only where the public forms tab is (hooks/usePublicForms
 * status 'on').
 */
const rulesUrl = (collectionId, source) => (source?.kind === 'link'
	? `${API_COLLECTIONS_URL}/${collectionId}/share-links/${source.id}/review-rules`
	: `${API_COLLECTIONS_URL}/${collectionId}/public-forms/${source.id}/review-rules`);

/** One rule as the API takes it: its id when it has one (kept), never the read-only `broken`. */
export function rulePayload(rule) {
	const out = {
		name: `${rule?.name || ''}`.trim(),
		when: `${rule?.when || ''}`.trim(),
		decision: rule?.decision === 'accept' ? 'accept' : 'reject',
	};
	if (rule?.id) out.id = rule.id;
	if (out.decision === 'reject' && `${rule?.reason || ''}`.trim()) out.reason = `${rule.reason}`.trim();
	return out;
}

/**
 * `PUT …/review-rules` — the WHOLE ordered list (the order is the meaning;
 * `[]` stops deciding by rule). → the form (or the link), with its `review`.
 * `acknowledgedPublishing` is the manager's plain-words yes, which the API
 * requires for an accept rule on a collection anyone can read.
 */
export async function saveReviewRules(collectionId, source, { rules, acknowledgedPublishing, maxAutoAcceptPerDay } = {}) {
	const payload = { rules: (rules || []).map(rulePayload) };
	if (acknowledgedPublishing === true) payload.acknowledgedPublishing = true;
	if (Number.isInteger(maxAutoAcceptPerDay)) payload.maxAutoAcceptPerDay = maxAutoAcceptPerDay;
	const { data } = await api.put(rulesUrl(collectionId, source), payload);
	return data;
}

/**
 * `POST …/review-rules/preview` — the DRY RUN: what these rules (or, with
 * none sent, the saved ones) would do to the queue and to the last 100
 * decided submissions. Writes nothing.
 */
export async function previewReviewRules(collectionId, source, { rules } = {}) {
	const payload = Array.isArray(rules) ? { rules: rules.map(rulePayload) } : {};
	const { data } = await api.post(`${rulesUrl(collectionId, source)}/preview`, payload);
	return data;
}
